import React, { CSSProperties, ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import { UserRoleEnum, useGetProjectQuery } from '../../../generated/graphql';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import NewProjectAdminDialog from '../../../components/dialogs/NewProjectAdminDialog/NewProjectAdminDialog';
import NewProjectMemberDialog from '../../../components/dialogs/NewProjectMemberDialog/NewProjectMemberDialog';
import { ticksToDate } from '../../../Utils';
import DeleteUserPermissionDialog from '../../../components/dialogs/DeleteUserPermissionDialog/DeleteUserPermissionDialog';
import SpinnerNew from '../../../components/main/SpinnerNew/SpinnerNew';
import { Plus, Trash2, Shield, Users, LayoutGrid, List } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';

type AccessControlProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const getInitials = (name?: string, email?: string) => {
    if (name) {
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
    }
    return email ? email.substring(0, 2).toUpperCase() : '??';
};

const AccessControl = (props: AccessControlProps) => {
    const { projectId } = useParams();
    const [activeTab, setActiveTab] = useState<'admins' | 'members'>('admins');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
    const [newProjectAdminVisible, setNewProjectAdminVisible] = useState(false);
    const [newProjectMemberVisible, setNewProjectMemberVisible] = useState(false);
    const [deleteMembersVisible, setDeleteMembersVisible] = useState(false);
    const [deleteAdminsVisible, setDeleteAdminsVisible] = useState(false);
    const [deleteSingleId, setDeleteSingleId] = useState<string | null>(null);

    const project = useGetProjectQuery({
        variables: {
            id: projectId
        }
    });

    const handleOnClose = () => {
        project.refetch();
        setNewProjectAdminVisible(false);
        setNewProjectMemberVisible(false);
        setDeleteAdminsVisible(false);
        setDeleteMembersVisible(false);
        setDeleteSingleId(null);
    };

    const isAdmin = project.data?.Project?.isAdmin || false;
    const selectedIds = activeTab === 'admins' ? selectedAdminIds : selectedMemberIds;
    const admins = (project.data?.Project?.admins as any[]) || [];
    const members = (project.data?.Project?.members as any[]) || [];
    const currentUsers = activeTab === 'admins' ? admins : members;
    const currentSelectedIds = activeTab === 'admins' ? selectedAdminIds : selectedMemberIds;
    const setCurrentSelectedIds = activeTab === 'admins' ? setSelectedAdminIds : setSelectedMemberIds;

    const tabs = [
        { id: 'admins' as const, label: 'Admins', icon: Shield },
        { id: 'members' as const, label: 'Members', icon: Users },
    ];

    const columns = [
        { id: 'email', name: 'Email', sortable: true },
        { id: 'updatedTime', name: 'Last Activity', sortable: true, render: (row: any) => <>{ticksToDate(row?.updatedTime)}</> }
    ];

    const renderCardGrid = (users: any[], selected: string[], setSelected: (fn: (prev: string[]) => string[]) => void, roleLabel: string) => (
        <div className="flex-1 overflow-auto p-4">
            {project.loading ? (
                <SpinnerNew />
            ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16" style={{ color: 'var(--text-tertiary)' }}>
                    {activeTab === 'admins' ? <Shield size={32} className="mb-3 opacity-50" /> : <Users size={32} className="mb-3 opacity-50" />}
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No {roleLabel.toLowerCase()}</p>
                    <p className="text-xs mt-1">Add {roleLabel === 'Admins' ? 'an admin' : 'a member'} to this project</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
                    {users.map((user: any) => {
                        const isSelected = selected.includes(user.id);
                        return (
                            <div
                                key={user.id}
                                className="card p-5 group relative transition-all"
                                style={{
                                    borderColor: isSelected ? 'var(--accent)' : undefined,
                                    backgroundColor: isSelected ? 'var(--accent-soft)' : undefined,
                                }}
                            >
                                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                    <div className="absolute top-4 right-4 flex items-center gap-2">
                                        <button
                                            className="btn btn-ghost p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{ color: 'var(--danger)' }}
                                            onClick={() => setDeleteSingleId(user.id)}
                                            title={`Remove ${roleLabel.toLowerCase().slice(0, -1)}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {
                                                setSelected(prev =>
                                                    prev.includes(user.id) ? prev.filter((i: string) => i !== user.id) : [...prev, user.id]
                                                );
                                            }}
                                            className="w-4 h-4 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{
                                                accentColor: 'var(--accent)',
                                                opacity: isSelected ? 1 : undefined,
                                            }}
                                        />
                                    </div>
                                </ShowOnRole>

                                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3 text-sm font-semibold"
                                    style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}>
                                    {getInitials(user.name, user.email)}
                                </div>

                                {user.name && (
                                    <p className="text-sm font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                                        {user.name}
                                    </p>
                                )}
                                <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                                    {user.email}
                                </p>
                                {user.updatedTime && (
                                    <p className="text-xs mt-2 pt-2 border-t" style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-subtle)' }}>
                                        Last active: {ticksToDate(user.updatedTime)}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    return (
        <div className="flex flex-col h-full">
            {/* Tab bar */}
            <div className="flex items-center px-4 pt-3 pb-1">
                <div className="tab-bar" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={activeTab === tab.id ? 'tab-item-active' : 'tab-item'}
                            >
                                <Icon size={14} strokeWidth={1.5} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
                <div className="flex-1" />
                <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-100)' }}>
                    <button
                        className="p-2 rounded-md transition-all"
                        style={{
                            backgroundColor: viewMode === 'grid' ? 'var(--bg-card)' : 'transparent',
                            boxShadow: viewMode === 'grid' ? 'var(--shadow-sm)' : 'none',
                            color: viewMode === 'grid' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            border: 'none', cursor: 'pointer', outline: 'none',
                        }}
                        onClick={() => setViewMode('grid')}
                    >
                        <LayoutGrid size={16} />
                    </button>
                    <button
                        className="p-2 rounded-md transition-all"
                        style={{
                            backgroundColor: viewMode === 'list' ? 'var(--bg-card)' : 'transparent',
                            boxShadow: viewMode === 'list' ? 'var(--shadow-sm)' : 'none',
                            color: viewMode === 'list' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            border: 'none', cursor: 'pointer', outline: 'none',
                        }}
                        onClick={() => setViewMode('list')}
                    >
                        <List size={16} />
                    </button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => project.refetch()} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => activeTab === 'admins' ? setNewProjectAdminVisible(true) : setNewProjectMemberVisible(true)}>
                        <Plus size={15} /> {activeTab === 'admins' ? 'New Admin' : 'New Member'}
                    </button>
                    {selectedIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => activeTab === 'admins' ? setDeleteAdminsVisible(true) : setDeleteMembersVisible(true)}>
                                <Trash2 size={15} /> Remove ({selectedIds.length})
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {viewMode === 'list' ? (
                    <>
                        {activeTab === 'admins' && (
                            <DataTableNew
                                sortingMode="client"
                                isLoading={project.loading}
                                data={admins}
                                showName={false}
                                searchable={false}
                                columns={columns}
                                onSelected={(ids) => setSelectedAdminIds(ids)}
                            />
                        )}
                        {activeTab === 'members' && (
                            <DataTableNew
                                sortingMode="client"
                                isLoading={project.loading}
                                data={members}
                                showName={false}
                                searchable={false}
                                columns={columns}
                                onSelected={(ids) => setSelectedMemberIds(ids)}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {activeTab === 'admins' && renderCardGrid(admins, selectedAdminIds, setSelectedAdminIds, 'Admins')}
                        {activeTab === 'members' && renderCardGrid(members, selectedMemberIds, setSelectedMemberIds, 'Members')}
                    </>
                )}
            </div>

            {newProjectAdminVisible && <NewProjectAdminDialog visible={newProjectAdminVisible} onClose={handleOnClose} projectId={projectId ?? ""} />}
            {newProjectMemberVisible && <NewProjectMemberDialog visible={newProjectMemberVisible} onClose={handleOnClose} projectId={projectId ?? ""} />}
            {deleteAdminsVisible && <DeleteUserPermissionDialog admin visible={deleteAdminsVisible} onClose={handleOnClose} projectId={projectId ?? ""} ids={selectedAdminIds} />}
            {deleteMembersVisible && <DeleteUserPermissionDialog visible={deleteMembersVisible} onClose={handleOnClose} projectId={projectId ?? ""} ids={selectedMemberIds} />}
            {deleteSingleId && <DeleteUserPermissionDialog admin={activeTab === 'admins'} visible={!!deleteSingleId} onClose={handleOnClose} projectId={projectId ?? ""} ids={[deleteSingleId]} />}
        </div>
    );
};

export default AccessControl;
